/* Core & libraries */
import React, { useEffect, useRef, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useTheme } from "react-jss";
import { ErrorMessage, Field, Formik } from "formik";

/* Helpers, constants, custom methods, internal libraries */
import { useGeneralService } from "../../../services/generalService";
import { useUserContext } from "../../../contexts/userContext";
import { useFareAccessService } from "../../../services/fareAccessService";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useCompaniesService } from "../../../services/companiesService";

/* Components */
import UCButton from "../../buttons";
import UCModal from "../../ucmodal";
import { createValidationPolicyYupSchema, useValidationService } from "../../../resources/validations/validations";
import MultiSelectField from "./multiselectField/index"
import FareAccessTabSkeleton from "../skeletonFareAccessTab";
import Toggle from "react-toggle";
import ASTextLoadingSpinner from "../../spinners/asTextLoadingSpinner";

/* Styles */
import styles from "./styles.js";
import { colors } from "../../../constants/colors";
import generalStyles from "../../../resources/styles/general";


const messages = defineMessages({
  fareAccessContractPlaceholderMessage: {
    id: 'fareAccess.toggle.contractID'
  },
})

const FareAccessTab = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();
  const { checkPermission } = useHasPermission();
  const { getStates } = useGeneralService();
  const { getFareAccessData, postFareAccessData, getDiscountTermsContent } = useFareAccessService();
  const { getCompanyDetails } = useCompaniesService();
  const userDetails = useUserContext();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;

  const { fareAccessValidationObj } = useValidationService()
  const [fareAccessSchema, setFareAccessSchema] = useState({});
  const [fareAccessData, setFareAccessData] = useState(null)
  const [contractIdError, setContractIdError] = useState(false)
  const [stateGovermenError, setStateGovermenError] = useState(false)
  const [statesArray, setStatesArry] = useState([]);
  const [initialSelected, setInitialSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [discountTermsContent, setDiscountTermsContent] = useState(null);
  const [isLoadingDiscountTerms, setIsLoadingDiscountTerms] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);

  useEffect(() => {
    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(fareAccessValidationObj);
    setFareAccessSchema(result)
  }, [])

  useEffect(() => {
    (async () => {
      const response = await getFareAccessData(companyEntityId);
      if (response) {
        setFareAccessData(response)
      }
    })();
  }, [])

  useEffect(() => {
    (async () => {
      const response = await getStates("US");
      if (response) {
        setStatesArry(response.states)
      }
    })();
  }, [])

  useEffect(() => {
    if (showModal) {
      (async () => {
        setIsLoadingDiscountTerms(true);
        const content = await getDiscountTermsContent();
        setDiscountTermsContent(content);
        setIsLoadingDiscountTerms(false);
      })();
    }
  }, [showModal]);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = async (values, setSubmitting) => {
    const selectedValues = values?.states?.map((states) => states.value);
    const payload = {
      federalFareOn: values.federalFareOn,
      stateFareOn: values.stateFareOn,
      states: selectedValues,
      corporateFareOn: values.corporateFareOn,
      contractId: values.contractId
    };

    if (values.corporateFareOn && !values.contractId) {
      setContractIdError(true);
    } else if (values.stateFareOn && values.states.length === 0) {
      setStateGovermenError(true);
    } else {
      const success = await postFareAccessData(companyEntityId, payload);
      if (success) {
        setFareAccessData(payload);

        // ⬇️ Reset companyDetails if corporateFare is turned OFF
        if (!values.corporateFareOn || !values.contractId?.trim()) {
          setCompanyDetails(null);
        }

        // ⬇️ If corporateFare is ON and contractId is present, refetch
        if (values.corporateFareOn && values.contractId?.trim()) {
          const updatedDetails = await getCompanyDetails(companyEntityId);
          if (updatedDetails) {
            setCompanyDetails({
              ...updatedDetails,
              companyId: companyEntityId,
            });
          }
        }
      }
    }
  };

  useEffect(() => {
    const mappedSelected = statesArray
      .filter((option) => fareAccessData?.states?.includes(option.value))
      .map((option) => ({ value: option.value, label: option.label }));
    setInitialSelected(mappedSelected);
  }, [statesArray, fareAccessData]);

  useEffect(() => {
    (async () => {
      const response = await getCompanyDetails(companyEntityId);
      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      })
    })();
  }, [])

  const contractIdChange = (e, handleChange, setFieldValue) => {
    setContractIdError(false)
    const value = e.target.value;
    handleChange(e);
    setFieldValue("contractId", value);
  };

  return (
    <>
      <Formik
        innerRef={formRef}
        enableReinitialize={true}
        initialValues={{
          federalFareOn: fareAccessData?.federalFareOn,
          stateFareOn: fareAccessData?.stateFareOn,
          corporateFareOn: fareAccessData?.corporateFareOn,
          states: initialSelected,
          contractId: fareAccessData?.contractId
        }}
        validationSchema={fareAccessSchema}
        onSubmit={(values, { setSubmitting }) => {
          submitForm(values, setSubmitting);
        }}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          resetForm,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue
        }) => {
          return (
            <>
              <div className={classes.fareAccessDetails}>
                <div>
                  <div className={classes.title}>
                    <FormattedMessage id="settings.fareAccess.title" />
                  </div>
                  <div className={classes.description}>
                    <FormattedMessage id="settings.fareAccess.description" />
                  </div>
                </div>
                {
                  checkPermission("F_COMPANYSETTINGS_MANAGEFAREACCESS") &&
                  <div className={classes.btnContainer}>
                    <UCButton
                      type="default"
                      className={classes.buttonCancel}
                      onClick={(e) => {
                        resetForm()
                      }}
                      text={<FormattedMessage id="settings.cancel" />}
                    />
                    <UCButton
                      type="primary"
                      className={classes.button}
                      onClick={handleFormSubmit}
                      text={<FormattedMessage id="settings.save" />}
                      disabled={!dirty}
                      spinner={{
                        name: "updateFareAccessSpinner",
                        size: 20,
                        color: colors.white,
                      }}
                    />
                  </div>
                }
              </div>
              <div className={classes.dividerContainer}>
                <hr className={classes.border} />
              </div>
              {
                fareAccessData ?
                  <div className={classes.fareAccessTab}>
                    <form onSubmit={handleSubmit}>
                      <div className="row formDetailsBlock">
                        <div className="col-12 col-md-3 descText">
                          <div className={classes.personalDetails}>
                            <FormattedMessage id="fareAccess.details.title" />
                          </div>
                          <div className={classes.description}>
                            <FormattedMessage id="fareAccess.details.description" />
                          </div>
                        </div>
                        <div className="col-12 col-md-9">
                          <div className={classes.formContainer}>
                            <div className={`${classes.toggle} ${classes.toggleContainer}`}>
                              <Toggle
                                icons={null}
                                onChange={(e) => {
                                  setFieldValue("federalFareOn", e.target.checked);
                                }}
                                checked={values.federalFareOn}
                                disabled={!checkPermission("F_COMPANYSETTINGS_MANAGEFAREACCESS")}
                              />
                              <span><FormattedMessage id="fareAccess.toggle.federalGoverment" /></span>
                            </div>
                            <div className={classes.toggleContainer}>
                              <div className={classes.toggle}>
                                <Toggle
                                  icons={null}
                                  onChange={(e) => {
                                    setFieldValue("stateFareOn", e.target.checked);
                                    setFieldValue(
                                      "states", []
                                    );
                                    setStateGovermenError(false)
                                  }}
                                  checked={values.stateFareOn}
                                  disabled={!checkPermission("F_COMPANYSETTINGS_MANAGEFAREACCESS")}
                                />
                                <span><FormattedMessage id="fareAccess.toggle.stateGoverment" /></span>
                              </div>
                              {values.stateFareOn && (
                                <div className={classes.stateFare}>
                                  <Field
                                    name="states"
                                    component={MultiSelectField}
                                    options={statesArray}
                                    value={values}
                                    stateGovermenError={stateGovermenError}
                                    setStateGovermenError={setStateGovermenError}
                                    disabled={!checkPermission("F_COMPANYSETTINGS_MANAGEFAREACCESS")}
                                  />
                                  {stateGovermenError && (
                                    <div className={classes.textErrorStyle}>
                                      <FormattedMessage id="fareAccessValidation.stateGovermenValues.required" />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className={classes.toggleContainer}>
                              <div className={classes.toggle}>
                                <Toggle
                                  icons={null}
                                  onChange={(e) => {
                                    setFieldValue("corporateFareOn", e.target.checked);
                                    setFieldValue(
                                      "contractId",
                                      e.target.checked ? values.contractId : ""
                                    );
                                    setContractIdError(false)
                                  }}
                                  checked={values.corporateFareOn}
                                  disabled={!checkPermission("F_COMPANYSETTINGS_MANAGEFAREACCESS")}
                                />
                                <span><FormattedMessage id="fareAccess.toggle.contractID" /></span>
                              </div>
                              {values.corporateFareOn && companyDetails?.showDiscountTerms && (
                                <UCButton
                                  type="defaultLink"
                                  className={classes.contractDetailsLink}
                                  onClick={() => setShowModal(true)}
                                  text={<FormattedMessage id="fareAccess.toggle.contractFareDetailsModalLink" />}
                                />
                              )}
                              {values.corporateFareOn && (
                                <div className={classes.inputContainer}>
                                  <Field
                                    placeholder={intl.formatMessage(messages.fareAccessContractPlaceholderMessage)}
                                    type="text"
                                    name="contractId"
                                    id="contractId"
                                    maxLength="8"
                                    onChange={(e) =>
                                      contractIdChange(e, handleChange, setFieldValue)
                                    }
                                    className={
                                      (errors.contractId && touched.contractId) || contractIdError
                                        ? "input-error"
                                        : null
                                    }
                                    disabled={!checkPermission("F_COMPANYSETTINGS_MANAGEFAREACCESS")}
                                  />
                                  <ErrorMessage
                                    name="contractId"
                                    component="span"
                                    className={classes.error}
                                  />
                                  {contractIdError && (
                                    <div className={classes.textErrorStyle}>
                                      <FormattedMessage id="fareAccessValidation.contractIdValue.required" />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                  :
                  <FareAccessTabSkeleton />
              }
            </>
          )
        }
        }
      </Formik>
      <UCModal
        className={classes.contractFareDetailsModal}
        showModal={showModal}
        setShowModal={setShowModal}
        modalTitle={discountTermsContent?.CampaignName}
        modalContent={
          isLoadingDiscountTerms ? (
            <ASTextLoadingSpinner loadingText={true} />
          ) : !discountTermsContent?.CampaignRestrictionMemo ? (
            <div className={classes.errorContentWrapper}>
              <p className={classes.errorTitle}><FormattedMessage id="fareAccess.contractFareDetails.error.title" /></p>
              <p className={classes.errorText}>
                <FormattedMessage id="fareAccess.contractFareDetails.error.description" />
              </p>
              <UCButton
                type="default"
                className={classes.closeButton}
                onClick={() => setShowModal(false)}
                text={<FormattedMessage id="fareAccess.contractFareDetails.error.closeButtonText" />}
                spinner={{
                  name: "updateFareAccessSpinner",
                  size: 20,
                  color: colors.white,
                }}
              />
            </div>
          ) : discountTermsContent?.CampaignRestrictionMemo ? (
            <div
              className={classes.modalContentWrapper}
              dangerouslySetInnerHTML={{
                __html: discountTermsContent.CampaignRestrictionMemo
              }}
            />
          ) : null
        }

        dataTestId="contractFareDetailsModal"
      />
    </>
  );
};

export default FareAccessTab;
